/* Piano keyboard (SVG) with optional key marks and tap input. */
import { useState } from 'react';
import { mod, shortNote, shortPc } from '../core/theory';

export type KeyMarks = Record<number, { cls?: string; label?: string }>;

export interface KeyboardProps {
  from?: number;
  to?: number;
  marks?: KeyMarks;
  onPick?: (midi: number) => void;
  /** 'c' labels every C, 'all' labels every white key. */
  labels?: 'c' | 'all' | 'none';
}

const isBlack = (m: number) => [1, 3, 6, 8, 10].includes(mod(m, 12));

export function Keyboard({ from = 48, to = 72, marks = {}, onPick, labels = 'c' }: KeyboardProps) {
  const [tapped, setTapped] = useState<Record<number, boolean>>({});
  const whites: number[] = [];
  for (let m = from; m <= to; m++) if (!isBlack(m)) whites.push(m);
  const ww = 30;
  const wh = 112;
  const bw = 19;
  const bh = 70;
  const W = whites.length * ww + 2;
  const keys: { m: number; x: number; w: number; black: boolean }[] = [];
  whites.forEach((m, i) => {
    keys.push({ m, x: 1 + i * ww, w: ww - 1, black: false });
  });
  whites.forEach((m, i) => {
    if (m + 1 <= to && isBlack(m + 1)) keys.push({ m: m + 1, x: 1 + (i + 1) * ww - bw / 2, w: bw, black: true });
  });
  const tap = (m: number) => {
    onPick?.(m);
    setTapped((t) => ({ ...t, [m]: true }));
    setTimeout(() => setTapped((t) => ({ ...t, [m]: false })), 250);
  };
  const keyClass = (k: (typeof keys)[number]) => {
    const mk = marks[k.m];
    return `${k.black ? 'kb-black' : 'kb-white'}${mk ? ` ${mk.cls || 'm-note'}` : ''}${tapped[k.m] ? ' m-tap' : ''}`;
  };
  const label = (k: (typeof keys)[number]) => {
    const mk = marks[k.m];
    const x = k.x + k.w / 2;
    if (mk?.label)
      return (
        <text key={`l${k.m}`} x={x} y={k.black ? bh - 8 : wh - 10} className={`kb-label${k.black ? ' on-black' : ''}`}>
          {mk.label}
        </text>
      );
    if (!k.black && labels === 'c' && mod(k.m, 12) === 0)
      return (
        <text key={`l${k.m}`} x={x} y={wh - 10} className="kb-label dim">
          {shortNote(k.m)}
        </text>
      );
    if (labels === 'all' && !k.black)
      return (
        <text key={`l${k.m}`} x={x} y={wh - 10} className="kb-label dim">
          {shortPc(k.m % 12)}
        </text>
      );
    return null;
  };
  const rect = (k: (typeof keys)[number]) => (
    <rect
      key={k.m}
      x={k.x}
      y={1}
      width={k.w}
      height={k.black ? bh : wh}
      rx={k.black ? 3 : 4}
      className={keyClass(k)}
      data-midi={k.m}
      onPointerDown={
        onPick
          ? (e) => {
              e.preventDefault();
              tap(k.m);
            }
          : undefined
      }
    />
  );
  return (
    <div className="kb-wrap">
      <svg
        viewBox={`0 0 ${W} ${wh + 2}`}
        width={W}
        height={wh + 2}
        className={`keyboard${onPick ? ' pickable' : ''}`}
        role="img"
        aria-label="Piyano klavyesi"
      >
        {keys.filter((k) => !k.black).map(rect)}
        <g>{keys.filter((k) => k.black).map(rect)}</g>
        <g className="kb-labels">{keys.map(label)}</g>
      </svg>
    </div>
  );
}
